import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface GeneratedSpec {
  name: string;
  proposal: string;
  requirements: { name: string; content: string }[];
  tasks: string;
}

export interface Improvement {
  section: string;
  suggestion: string;
  priority: string;
}

export interface QualityResult {
  quality_score: number;
  issues: { area: string; severity: string; description: string }[];
  suggestions: string[];
  summary: string;
}

export interface ClaudeMdAnalysis {
  score: number;
  missing_sections: string[];
  outdated_refs: string[];
  suggestions: string[];
  summary: string;
}

export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** One passage the answer was grounded in, addressable by the reader. */
export interface RequirementGap {
  area: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
}

export interface RequirementGapsResult {
  gaps: RequirementGap[];
  summary: string;
}

export interface RuleConflictFinding {
  ruleId: string;
  ruleText: string;
  relation: 'duplicate' | 'conflict' | 'overlap';
  explanation: string;
}

export interface RuleConflictSuggestion {
  action: 'update_existing' | 'create_new';
  ruleId: string;
  suggestedText: string;
  rationale: string;
}

export interface RuleConflictResult {
  /** The rule reworded for clarity: spelling fixed, phrased for AI-agent use. */
  optimizedText: string;
  findings: RuleConflictFinding[];
  suggestions: RuleConflictSuggestion[];
  summary: string;
}

export interface RuleRefactorProposal {
  action: 'update' | 'delete';
  ruleId: string;
  currentText: string;
  currentCategory: string;
  /** New text/category — present on update proposals only. */
  text?: string;
  category?: string;
  reason: string;
}

export interface RuleRefactorResult {
  proposals: RuleRefactorProposal[];
  summary: string;
}

export interface ImageAnalysis {
  description: string;
  requirements: string[];
  mermaid: string | null;
}

/** A photo reference for document assembly (already in the tenant media store). */
export interface AssembleAttachment {
  mediaId: string;
  description?: string;
}

export interface AssembledDocument {
  name: string;
  content: string;
}

/** Poll an async AI job (3s interval, ~2min budget by default) until it resolves. */
async function pollJob<T>(jobId: string, failureMessage: string, attempts = 40): Promise<T> {
  for (let i = 0; i < attempts; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    const { data: status } = await api.get(`/ai/jobs/${jobId}`);
    if (status.status === 'completed') return status.result;
    if (status.status === 'failed') throw new Error(status.error || failureMessage);
  }
  throw new Error(`${failureMessage} (timed out)`);
}

let accessToken: (() => Promise<string | null>) | null = null;

export const aiApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    accessToken = getToken;
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  /** The signed-in header, for a client that sends its own requests (the Evrella widget). */
  async authHeaders(): Promise<Record<string, string>> {
    const token = await accessToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  async generateSpec(description: string): Promise<GeneratedSpec> {
    const { data: job } = await api.post('/ai/generate-spec', { description });
    return pollJob<GeneratedSpec>(job.jobId, 'Spec generation failed');
  },

  /** Vision analysis of a captured image (photo-capture Phase 2 job). */
  async analyzeImage(mediaId: string): Promise<ImageAnalysis> {
    const { data: job } = await api.post('/ai/analyze-image', { mediaId });
    return pollJob<ImageAnalysis>(job.jobId, 'Image analysis failed');
  },

  /**
   * Assemble one document from all captured elements (multi-element capture).
   * Text arrives inline; photos as tenant media ids the worker sends to the
   * model as vision blocks. Generation can take a while with photos — poll
   * with a 5-minute budget.
   *
   * `onJobStarted` reports the job id as soon as the worker is queued: the
   * notification bell keys everything about one generation on it, and the
   * caller cannot wait for this promise to learn it.
   */
  async assembleDocument(params: {
    templateName: string;
    templateContent: string;
    textContent: string;
    attachments?: AssembleAttachment[];
  }, onJobStarted?: (jobId: string) => void): Promise<AssembledDocument> {
    const { data: job } = await api.post('/ai/assemble-document', params);
    onJobStarted?.(job.jobId);
    return pollJob<AssembledDocument>(job.jobId, 'Document generation failed', 100);
  },

  async improveSpec(draftId: string): Promise<{ improvements: Improvement[]; summary: string }> {
    const { data } = await api.post(`/ai/improve-spec/${draftId}`);
    return data;
  },

  async analyzeQuality(repo?: string): Promise<QualityResult> {
    const { data } = await api.post('/ai/analyze-quality', { repo });
    return data;
  },

  async analyzeClaudeMd(repoFullName: string, path?: string): Promise<ClaudeMdAnalysis> {
    const { data } = await api.post('/ai/analyze-claude-md', { repoFullName, path });
    return data;
  },

  /** Analyze the tenant's captured requirements for coverage gaps. */
  async analyzeRequirementGaps(): Promise<RequirementGapsResult> {
    const { data } = await api.post('/ai/analyze-requirement-gaps');
    return data;
  },

  /** Check a proposed project rule against existing rules for duplicates and conflicts. */
  async analyzeRuleConflicts(text: string, category: string): Promise<RuleConflictResult> {
    const { data } = await api.post('/ai/analyze-rule-conflicts', { text, category });
    return data;
  },

  /** Analyze the full rule set and propose merges, deletions, and rewording. */
  async refactorRules(): Promise<RuleRefactorResult> {
    const { data } = await api.post('/ai/refactor-rules');
    return data;
  },

};
